import axios from 'axios'
import { useAuthStore } from '@/stores/auth'

// Default timeout for most API calls (30 seconds)
const DEFAULT_TIMEOUT = 30000

// Extended timeout for long-running operations like API tests (2 minutes)
const EXTENDED_TIMEOUT = 120000

// Konnect analytics (logs + best-effort metrics) can take up to ~120s server-side.
export const STATS_REQUESTS_TIMEOUT = 125000

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  // Important: send cookies with requests for session-based auth (Entra)
  withCredentials: true,
  // Default timeout - prevents requests from hanging indefinitely
  timeout: DEFAULT_TIMEOUT,
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const authStore = useAuthStore()

    // Don't retry auth endpoints - prevents infinite loop
    const isAuthEndpoint = originalRequest.url?.includes('/auth/')

    // Handle 401 errors - try to refresh token (but not for auth endpoints)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      // Only try refresh if we have a refresh token
      if (authStore.refreshToken) {
        try {
          await authStore.refresh()
          originalRequest.headers.Authorization = `Bearer ${authStore.token}`
          return api(originalRequest)
        } catch {
          authStore.logout()
          window.location.href = '/login'
          return Promise.reject(error)
        }
      }
    }

    return Promise.reject(error)
  }
)

export default api

// API service functions
export const vendorRegistrationApi = {
  validate: (params: { id: string; name: string; token: string }) =>
    axios.get('/api/v1/public/vendor-registration-form/validate', {
      params,
      withCredentials: false,
    }),
  submit: (data: FormData) =>
    axios.post('/api/v1/public/vendor-registration-form', data, {
      withCredentials: false,
      timeout: EXTENDED_TIMEOUT,
    }),
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refresh_token: refreshToken }),
}

export const environmentsApi = {
  list: () => api.get('/environments'),
  get: (id: string) => api.get(`/environments/${id}`),
  create: (data: object) => api.post('/environments', data),
  setup: (id: string) => api.post(`/environments/${id}/setup`),
}

export const instancesApi = {
  list: (params?: object) => api.get('/instances', { params }),
  get: (id: string) => api.get(`/instances/${id}`),
  create: (data: object) => api.post('/instances', data),
  update: (id: string, data: object) => api.put(`/instances/${id}`, data),
  delete: (id: string) => api.delete(`/instances/${id}`),
  provision: (id: string) => api.post(`/instances/${id}/provision`),
  deprovision: (id: string) => api.post(`/instances/${id}/deprovision`),
}

export const consumersApi = {
  list: (params?: object) => api.get('/consumers', { params }),
  get: (id: string) => api.get(`/consumers/${id}`),
  create: (data: object) => api.post('/consumers', data),
  update: (id: string, data: object) => api.patch(`/consumers/${id}`, data),
  delete: (id: string) => api.delete(`/consumers/${id}`),
  grantAccess: (id: string, accountIds: string[]) =>
    api.post(`/consumers/${id}/grant-access`, { account_ids: accountIds }),
  revokeAccess: (id: string, accountIds: string[]) =>
    api.post(`/consumers/${id}/revoke-access`, { account_ids: accountIds }),
}

export const bulkApi = {
  provisionInstances: (data: object) => api.post('/bulk/instances', data),
  createConsumers: (data: object) => api.post('/bulk/consumers', data),
}

export const auditApi = {
  list: (params?: object) => api.get('/audit', { params }),
  get: (id: string) => api.get(`/audit/${id}`),
}

export const operationsApi = {
  list: (params?: object) => api.get('/operations', { params }),
  get: (id: string) => api.get(`/operations/${id}`),
}

export const usersApi = {
  list: (params?: object) => api.get('/users', { params }),
  get: (id: string) => api.get(`/users/${id}`),
  create: (data: object) => api.post('/users', data),
  update: (id: string, data: object) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
  resendInvite: (id: string) => api.post(`/users/${id}/resend-invite`),
}

export const kswebApi = {
  listInstances: (params?: object) => api.get('/ksweb/instances', { params }),
  searchInstances: (query: string) => api.get('/ksweb/instances/search', { params: { q: query } }),
  getInstance: (id: string) => api.get(`/ksweb/instances/${id}`),
  getStats: () => api.get('/ksweb/stats'),
  refresh: () => api.post('/ksweb/refresh'),
}

export const jobsApi = {
  list: () => api.get('/jobs'),
  listByCategory: () => api.get('/jobs/categories'),
  getExecutions: (jobId?: string) => api.get('/jobs/executions', { params: jobId ? { job_id: jobId } : {} }),
  trigger: (jobId: string) => api.post(`/jobs/${jobId}/trigger`),
}

export const validationApi = {
  // Validate EMR authentication configuration
  validateEMRAuth: (instanceName: string) => 
    api.get(`/validation/emr-auth/${instanceName}`, { timeout: EXTENDED_TIMEOUT }),
  // Fix EMR authentication token mismatch
  fixEMRAuth: (instanceName: string) => 
    api.post(`/validation/emr-auth/${instanceName}/fix`, null, { timeout: EXTENDED_TIMEOUT }),
  // Test API key credential - uses extended timeout as this involves EMR validation + Kong API test
  testAPI: (instanceName: string, consumerID: string, credentialID: string) => 
    api.post('/validation/test-api', 
      { instance_name: instanceName, consumer_id: consumerID, credential_id: credentialID },
      { timeout: EXTENDED_TIMEOUT }
    ),
  // Test OAuth2 credential - uses extended timeout as this involves token fetch + EMR validation + API test
  testOAuth2: (instanceName: string, clientID: string, clientSecret: string) =>
    api.post('/validation/test-oauth2', 
      { instance_name: instanceName, client_id: clientID, client_secret: clientSecret },
      { timeout: EXTENDED_TIMEOUT }
    ),
  // Get OAuth2 credentials for testing (fetches stored credentials)
  getOAuth2Credentials: (accountId: string) => api.get(`/credentials/${accountId}/oauth2-test`),
}

